// src/context/PersonasView.jsx
//
// Reusable personas management body, keyed on an explicit tenantId (mirrors
// DocumentsView / ContextConfigView). Mounted two ways:
//   • tenant-admin console → TenantPersonasPage        (canWrite=isTenantAdmin)
//   • superadmin console   → admin/PersonasPage        (tenant dropdown, canWrite)
// NO member surface: the brief picker reads the same personas through
// useContextOptions and needs none of this.
//
// This file is the LIST: groups, rows and the On/Off switch. The
// editing itself lives in `./persona/` — `PersonaModal` hosts every action
// (the editor over GET|PUT /personas/{id}/sections, the manual wizard, the AI
// aids). Styles are shared through `./persona/personaStyles.js`.
//
// Two independent capabilities, deliberately NOT symmetrical:
//
//   TOGGLE  writes `disabled:` in the tenant's persona-toggles.yaml. It touches
//           no persona FILE, so it has no provenance or strict-mode fallout and
//           is safe to offer on every row, always.
//
//   EDIT    writes the persona file itself — see `./persona/PersonaEditor.jsx`.
//
// ⛔ GUARD 2 — STRICT MODE. `list_available_personas_with_source` returns the
// tenant's own persona files as soon as the tenant owns ANY of them, and the
// globals otherwise. So the FIRST write against a tenant that owns none would
// collapse its whole picker to the single persona just saved — silently, and
// with no API path back (only the operator CLI `tenant-scaffold-entities`).
// A row still reading `source: "global"` is exactly that pre-state, so we
// REFUSE to edit it and name the precondition instead. The same field gates
// "Nuova persona": creating is offered only where the door is already open.
// ⭐ In practice this should never fire: onboarding scaffolds all 13 personas
// into the tenant dir, so every provisioned tenant is already strict-mode. A
// row that does read "global" means the tenant is mis-provisioned, and the
// remedy really is the CLI, not a text editor.
// ⛔ And still NOT "scaffold them from here first". The reason CHANGED but the
// answer did not: an API-created file now carries a provenance row, so copying
// all 13 in would no longer lock all 13. What it would still do is flip the
// tenant to strict mode on the FIRST of the 13 writes, with no transaction
// behind the other 12 — so a failure halfway leaves the picker strict-mode over
// a partial set, which is a worse state than the mis-provisioning it was meant
// to repair. That path wants a server-side scaffold endpoint, not a loop in a
// browser tab. It remains operator CLI work.
//
// ⛔ LEGACY ROWS. A persona that declares no `market` predates the four-section
// model (locally: every persona of 10 of the 14 tenants). The editor has no
// shape to open such a file with — the GET answers 409 — so the row says it has
// to be rebuilt and offers no Modifica at all, rather than opening a panel
// whose only possible outcome is a refusal. Rebuilding one is a content rewrite
// and therefore backend work, requested separately.
//
// The list load and the switch's refusal are worded by contextErrorCopy (a known code keeps its sentence —
// CANNOT_DISABLE_LAST_PERSONA included — anything else prints the code). Every editor refusal is keyed on the code.
//
// AI AIDS, opt-in through `assist` (only /tenant-admin/personas passes it; the superadmin mount never
// does, so a superadmin cannot spend a workspace's credits by accident). With it: "Suggerisci personas con l'AI"
// and the job card above the list, "Generala con l'AI" inside "Nuova persona", and "Adatta con l'AI" on every
// editable, non-legacy row. `lastBody` keeps the last job request for the card's Riprova, which is offered only
// while it is held. A Propose "enable" row turns that persona on; a tenant change resets every AI state.
//
// v198 — NOTHING opens inline any more. Every action (Suggerisci, Nuova persona, Modifica, Adatta con l'AI, a
// job's "Apri la proposta") sets `flow` and runs inside ONE modal (persona/PersonaModal.jsx); the page keeps the
// list, the action row, the job card and a notice after a create / review save. The page also owns what must
// survive the modal closing: the proposals (`proposeState`), the job (`usePersonaJob`, the ONE poller) and the
// "Avanzate" preference (`useAdvancedView('personas', userKey)`, shared by every modal of this page).
// Opening a proposal no longer dismisses the job card; saving it does.
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Pencil } from 'lucide-react';
import { COLORS, FONT_MONO, SEMANTIC } from '../constants/theme.js';
import { listPersonas, setPersonaEnabled } from './contextApi.js';
import { contextErrorCopy } from './contextErrorCopy.js';
import PersonaModal from './persona/PersonaModal.jsx';
import JobCard from './persona/assist/JobCard.jsx';
import usePersonaJob from './persona/assist/usePersonaJob.js';
import useAdvancedView from '../hooks/useAdvancedView.js';
// Phosphor Regular (the Brian UI icon set), kept as SVG files and drawn in `currentColor`.
import { ReactComponent as SparkleIcon } from '../assets/icons/sparkle.svg';
import { ReactComponent as UserTargetIcon } from '../assets/icons/user-target.svg';
import { ReactComponent as MagicWandIcon } from '../assets/icons/magic-wand.svg';
import {
  accentOutlineBtn, actionRow, alertBox, editIconBtn, emptyBox, groupHeading, listStyle,
  primaryBtn, rowStyle, toggleKnob, toggleTrack,
} from './persona/personaStyles.js';

// `label ?? prettify(id)` is the contract's display rule — the BE is
// authoritative for labels, and a persona file that omits `display_name` sends
// null rather than a fabricated string.
function prettify(id) {
  return String(id).replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

// An action button with a leading icon (as "Crea nuovo utente" in Gestione Workspace): icon + label on one line.
const withIcon = (style) => ({ ...style, display: 'inline-flex', alignItems: 'center', gap: '8px' });

const idStyle = { fontSize: '12px', color: COLORS.textTertiary, fontFamily: FONT_MONO };

export default function PersonasView({ tenantId, canWrite = false, assist = false, userKey = '' }) {
  const { t, i18n } = useTranslation(['context', 'common']);
  // load() words its refusal with the CURRENT t without depending on it (a t
  // that changes identity would re-run the list read).
  const tRef = useRef(t);
  tRef.current = t;
  const uiLanguage = i18n.language?.split('-')[0] === 'en' ? 'en' : 'it';

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [toggling, setToggling] = useState('');   // persona id with a PUT in flight
  const [flow, setFlow] = useState(null);           // the modal's process (PersonaModal); null = closed
  const [notice, setNotice] = useState(null);       // { key, params } after a create / review save
  const aiOn = Boolean(assist && canWrite);
  // `rowsReady`: the list is LOADED — only then can a create job be judged as saved or not. A failed load leaves
  // `rows` empty (and a workspace always has at least one persona), so an empty list never counts as ready;
  // `error` is not used because a refused toggle sets it too.
  const jobApi = usePersonaJob({ tenantId, enabled: aiOn, rows, rowsReady: !loading && rows.length > 0 });
  const [lastBody, setLastBody] = useState(null);             // the last job request, for Riprova
  // The request, the ticked documents and the last proposals: kept while the modal is closed and reopened.
  const [proposeState, setProposeState] = useState({ request: '', selected: [], result: null });
  const [advanced, setAdvanced] = useAdvancedView('personas', userKey);

  const load = useCallback(async () => {
    if (!tenantId) { setLoading(false); return; }
    setLoading(true);
    setError('');
    try {
      // `include_disabled` only when we may write: it costs an extra admin gate
      // (403 TENANT_ADMIN_REQUIRED) that a read-only mount would fail on, and
      // it is what makes the server list the disabled rows at all.
      const data = await listPersonas({
        tenant: tenantId, includeDisabled: canWrite, language: uiLanguage,
      });
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(contextErrorCopy(tRef.current, e, {
        server: 'fallback.server', network: 'fallback.network', generic: 'personas.errors.loadFailed',
      }));
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [tenantId, canWrite, uiLanguage]);

  useEffect(() => { load(); }, [load]);

  // Close an open modal when the tenant changes underneath it, so the SA
  // dropdown can never leave one tenant's persona open under another's heading.
  useEffect(() => {
    setFlow(null); setNotice(null); setLastBody(null);
    setProposeState({ request: '', selected: [], result: null });
  }, [tenantId]);

  // Resolves true once the job is started (the modal then moves to "Generazione").
  async function startJob(body) {
    setLastBody(body);
    return jobApi.start(body);
  }

  // Resolves true when the persona is now on, false when the switch was refused.
  async function handleToggle(row) {
    setToggling(row.id);
    setError('');
    try {
      const updated = await setPersonaEnabled({
        tenant: tenantId, id: row.id, enabled: !row.enabled, language: uiLanguage,
      });
      // Plain merge: the reply carries no provenance, and no UI reads it any more.
      setRows(prev => prev.map(r => (r.id === updated.id ? { ...r, ...updated } : r)));
      return true;
    } catch (e) {
      // 409 CANNOT_DISABLE_LAST_PERSONA lands here (its own sentence). There is no
      // `force` and no `details`: say why and leave the row exactly as it was.
      setError(contextErrorCopy(t, e, {
        server: 'fallback.server', network: 'fallback.network', generic: 'personas.errors.toggleFailed',
      }));
      return false;
    } finally {
      setToggling('');
    }
  }

  // A Propose "enable" row: the outcome is shown on the proposal row (the page line is behind the overlay).
  async function enableFromProposal(id) {
    const r = rows.find(x => x.id === id);
    if (!r) return false;
    if (r.enabled !== false) return true;
    return handleToggle(r);
  }

  const labelOf = (id) => {
    const r = rows.find(x => x.id === id);
    return (r && r.label) || prettify(id || '');
  };

  // Modifica stays open ("Salvato."); a create or a review save closes the modal with a page notice, and a
  // review save also retires the job card's offer.
  function handleSaved({ flow: f, id, name }) {
    if (f.kind === 'edit') { load(); return; }
    if (f.kind === 'review') jobApi.dismiss();
    setFlow(null);
    const created = f.kind === 'manual' || (f.kind === 'review' && f.job.kind === 'create');
    setNotice({
      key: created ? 'personas.modal.created' : 'personas.modal.saved',
      params: { name: name || labelOf(id) },
    });
    load();
  }

  // Every entry point opens the modal on a fresh page: an old notice would describe a previous process.
  function openFlow(next) {
    setNotice(null);
    setFlow(next);
  }

  // Group on `market`, which is language-invariant. ⚠️ An old-format tenant
  // declares none on ANY persona — render one flat list rather than a lone
  // "senza mercato" heading over every single row. Order: b2b · any · b2c, then
  // the rest; `any` ("Qualsiasi mercato") is a real market with its own group.
  const groups = useMemo(() => {
    const by = (m) => rows.filter(r => r.market === m);
    const known = ['b2b', 'any', 'b2c'];
    const rest = rows.filter(r => !known.includes(r.market));
    if (known.every(m => by(m).length === 0)) return [{ key: '', rows: rest }];
    return [
      ...known.map(m => ({ key: m, rows: by(m) })),
      { key: 'other', rows: rest },
    ].filter(g => g.rows.length > 0);
  }, [rows]);

  // GUARD 2, for creation: the first persona file a tenant owns flips strict
  // mode, so on a tenant that owns none "Nuova persona" would not add a 14th —
  // it would leave the tenant with exactly ONE and no API path back.
  const tenantOwnsAny = rows.some(r => r.source !== 'global');

  if (!tenantId) return <div style={emptyBox}>{t('noTenant')}</div>;

  return (
    <div>
      {notice && <div role="status" style={alertBox(SEMANTIC.verde.fg)}>{t(notice.key, notice.params)}</div>}
      {error && <div role="alert" style={alertBox(SEMANTIC.errore.fg)}>{error}</div>}

      {aiOn && (
        <JobCard
          job={jobApi.job}
          view={jobApi.view}
          review={jobApi.review}
          // While the modal is open the refusal is shown inside it, by the form that started the job.
          startError={flow ? null : jobApi.startError}
          onOpen={(job) => openFlow({ kind: 'review', origin: 'card', job })}
          onDismiss={jobApi.dismiss}
          onRetry={lastBody ? () => startJob(lastBody) : undefined}
        />
      )}

      {loading ? (
        <div style={emptyBox}>{t('personas.loading')}</div>
      ) : rows.length === 0 ? (
        <div style={emptyBox}>{t('personas.empty')}</div>
      ) : (
        <>
          {/* No intro line above the list: what each control does is said by its own tooltip. */}
          <div style={actionRow}>
            {aiOn && (
              <button
                type="button" aria-haspopup="dialog" style={withIcon(accentOutlineBtn(false))}
                title={t('personas.tooltips.propose')}
                // Always on "Descrivi", pre-filled with the last request and documents; the previous list is one click
                // away ("Vedi le proposte precedenti"), since it goes stale as soon as the user acts on it.
                onClick={() => openFlow({ kind: 'propose', step: 'describe' })}
              >
                <SparkleIcon width={16} height={16} aria-hidden="true" focusable="false" />
                {t('personas.assist.proposeOpen')}
              </button>
            )}
            {canWrite && tenantOwnsAny && (
              <button
                type="button" aria-haspopup="dialog" style={{ ...withIcon(primaryBtn(false)), marginLeft: 'auto' }}
                title={t('personas.tooltips.create')}
                onClick={() => openFlow(aiOn ? { kind: 'method' } : { kind: 'manual', origin: 'direct' })}
              >
                <UserTargetIcon width={16} height={16} aria-hidden="true" focusable="false" />
                {t('personas.create.button')}
              </button>
            )}
          </div>

          {groups.map(group => (
            <section key={group.key || 'all'} style={{ marginBottom: '24px' }}>
              {group.key && (
                <h2 style={groupHeading}>{t(`personas.markets.${group.key}`)}</h2>
              )}
              <div style={listStyle}>
                {group.rows.map(row => (
                  <PersonaRow
                    key={row.id}
                    row={row}
                    canWrite={canWrite}
                    busy={toggling === row.id}
                    // ⛔ EVERY switch locks while ANY toggle is in flight, not
                    // just the one clicked. The backend read-modify-writes a
                    // single `disabled:` list per tenant with no ETag, so two
                    // toggles for DIFFERENT personas race and one is silently
                    // lost — the screen would then show both as changed while
                    // the server kept one. Serializing costs nothing at this
                    // scale and is the only defence the FE has.
                    locked={Boolean(toggling)}
                    onToggle={() => handleToggle(row)}
                    onEdit={() => openFlow({ kind: 'edit', personaId: row.id })}
                    assist={aiOn}
                    jobBusy={jobApi.busy}
                    onPersonalize={() => openFlow({ kind: 'personalize', origin: 'row', personaId: row.id })}
                    t={t}
                  />
                ))}
              </div>
            </section>
          ))}
        </>
      )}

      <PersonaModal
        flow={flow} setFlow={setFlow} tenantId={tenantId} canWrite={canWrite} rows={rows} labelOf={labelOf}
        jobApi={jobApi} startJob={startJob} lastBody={lastBody}
        proposeState={proposeState} setProposeState={setProposeState} onEnable={enableFromProposal}
        advanced={advanced} onAdvancedChange={setAdvanced}
        onSaved={handleSaved} onClose={() => setFlow(null)}
      />
    </div>
  );
}

// ── One row: name, id, edit toggle, On/Off switch ─────────────────────

function PersonaRow({
  row, canWrite, busy, locked, onToggle, onEdit,
  assist = false, jobBusy = false, onPersonalize, t,
}) {
  const enabled = row.enabled !== false;
  // See GUARD 2 — a global row is the pre-strict-mode state, and editing it
  // would hide every other persona from the tenant.
  const editable = row.source !== 'global';
  // See LEGACY ROWS — no market means a file the editor cannot open at all.
  const legacy = row.market == null;
  // Only an ENABLED persona offers Modifica and "Adatta": a disabled one is out of the Content Generator, so the
  // row shows just its switch — turning it on brings the two buttons back. (The switch is the rightmost control,
  // so nothing moves under the pointer.)
  const actionable = canWrite && editable && !legacy && enabled;

  return (
    <div style={rowStyle()}>
      <div style={{ minWidth: 0, flex: 1 }}>
        {/* A switched-off persona reads as inactive: its name in the muted text colour (the id is muted already). */}
        <div style={{ fontSize: '14px', fontWeight: 600, color: enabled ? COLORS.ink : COLORS.textTertiary }}>
          {row.label || prettify(row.id)}
        </div>
        <div style={idStyle}>{row.id}</div>
      </div>

      {canWrite && !editable && (
        <span style={{ fontSize: '12px', color: COLORS.textSecondary }}>
          {t('personas.notEditable')}
        </span>
      )}

      {canWrite && editable && legacy && (
        <span title={t('personas.legacyRowHint')} style={{ fontSize: '12px', color: COLORS.textSecondary }}>
          {t('personas.legacyRow')}
        </span>
      )}

      {assist && actionable && (
        <button
          type="button"
          onClick={onPersonalize}
          disabled={jobBusy}
          aria-haspopup="dialog"
          aria-label={t('personas.assist.personalizeOpenFor', { name: row.label || prettify(row.id) })}
          // An icon button like the pencil beside it: the words ride in the tooltip and the accessible name.
          title={t('personas.tooltips.personalize')}
          style={{ ...editIconBtn(false), ...(jobBusy ? { opacity: 0.5, cursor: 'not-allowed' } : null) }}
        >
          <MagicWandIcon width={16} height={16} aria-hidden="true" focusable="false" />
        </button>
      )}

      {actionable && (
        // The classic pencil; its word ("Modifica") rides in the accessible
        // name and the tooltip. It opens the editor in the modal.
        <button
          type="button"
          onClick={onEdit}
          aria-label={t('personas.edit')}
          aria-haspopup="dialog"
          title={t('personas.tooltips.edit')}
          style={editIconBtn(false)}
        >
          <Pencil size={15} aria-hidden="true" />
        </button>
      )}

      {canWrite ? (
        <button
          type="button"
          role="switch"
          aria-checked={enabled}
          aria-busy={busy || undefined}
          aria-label={t('personas.toggleLabel', { name: row.label || prettify(row.id) })}
          // The tooltip says what the CURRENT position means for the Content Generator.
          title={enabled ? t('personas.tooltips.switchOn') : t('personas.tooltips.switchOff')}
          disabled={busy || locked}
          onClick={onToggle}
          style={toggleTrack(enabled, busy || locked)}
        >
          <span aria-hidden="true" style={toggleKnob(enabled)} />
        </button>
      ) : (
        <span
          role="img"
          aria-label={enabled ? t('personas.on') : t('personas.off')}
          title={enabled ? t('personas.on') : t('personas.off')}
          style={{ ...toggleTrack(enabled, false), cursor: 'default' }}
        >
          <span aria-hidden="true" style={toggleKnob(enabled)} />
        </span>
      )}
    </div>
  );
}
